/* Haarspullen search enhancement. No dependencies, tracking, or persistent storage. */
(() => {
  'use strict';
  const normalize = (value) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  const safeDestination = (raw, base) => {
    if (!raw) return null;
    try {
      const url = new URL(raw, base);
      return url.origin === new URL(base).origin && /^https?:$/.test(url.protocol) ? url.href : null;
    } catch { return null; }
  };
  // Prepared once per file; candidates are indexed by the first two characters.
  const prepareRules = (rows) => rows.flatMap(row => {
    if (!row || row.enabled === false || typeof row.title !== 'string' || !row.title.trim()) return [];
    const triggers = (Array.isArray(row.triggers) ? row.triggers : String(row.triggers || '').split(/[,\n]/))
      .filter(t => typeof t === 'string').map(normalize).filter(t => t.length >= 2 && t.length <= 200);
    if (!triggers.length || row.title.length > 200) return [];
    const destination = typeof row.destination === 'string' ? row.destination.trim() : '';
    if (destination && !safeDestination(destination, location.href)) return [];
    return [{title:row.title.trim(), key:normalize(row.title), triggers:[...new Set(triggers)],
      query:typeof row.query === 'string' && row.query.trim() ? row.query.trim().slice(0,300) : row.title.trim(),
      destination, priority:Number.isFinite(Number(row.priority)) ? Number(row.priority) : 100}];
  });
  const indexRules = rules => {
    const index = new Map();
    for (const rule of [...rules].sort((a,b) => a.priority - b.priority)) {
      for (const prefix of new Set(rule.triggers.map(t => t.slice(0,2)))) {
        if (!index.has(prefix)) index.set(prefix, []);
        index.get(prefix).push(rule);
      }
    }
    return index;
  };
  const findRules = (index, term) => {
    const normalized = normalize(term);
    if (normalized.length < 2) return [];
    const found = [], seen = new Set();
    for (const rule of index.get(normalized.slice(0,2)) || []) {
      if (seen.has(rule.key) || !rule.triggers.some(t => t.startsWith(normalized))) continue;
      seen.add(rule.key); found.push(rule);
      if (found.length === 4) break;
    }
    return found;
  };
  const suggestionFiles = new Map();
  const loadSuggestionFile = (url, language) => {
    const key = url + '|' + language;
    const cached = suggestionFiles.get(key);
    if (cached && (!cached.failed || Date.now() - cached.time < 30000)) return cached.promise;
    const entry = {time:Date.now(), failed:false};
    entry.promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(url, {signal:controller.signal, credentials:'omit'});
        if (!response.ok) throw new Error('Suggestions unavailable');
        const text = await response.text();
        if (text.length > 2000000) throw new Error('Suggestion file too large');
        const data = JSON.parse(text);
        if (data.version !== 1 || data.language !== language || !Array.isArray(data.suggestions) || data.suggestions.length > 5000) throw new Error('Invalid suggestion file');
        return prepareRules(data.suggestions);
      } catch {
        entry.failed = true; entry.time = Date.now(); return [];
      } finally { clearTimeout(timeout); }
    })();
    suggestionFiles.set(key, entry); return entry.promise;
  };
  function mount(form) {
    if (form.dataset.hsMounted) return;
    form.dataset.hsMounted = 'true';
    const input = form.querySelector('#search_main');
    const panel = form.querySelector('#livesearch');
    const status = form.querySelector('[role="status"]');
    const labels = form.querySelector('[data-hs-labels]').dataset;
    const emptyHTML = panel.innerHTML;
    const enabled = form.dataset.enabled !== 'false';
    const inlineRules = prepareRules(Array.from(form.querySelector('[data-hs-rules]').content.querySelectorAll('a')).map(el => ({
      title: el.textContent.trim(), triggers: (el.dataset.triggers || '').split(/[,\n]/).filter(Boolean),
      query: el.dataset.query || el.textContent.trim(), destination: el.dataset.destination, priority: Number(el.dataset.priority) || 100
    })));
    let ruleIndex = indexRules(inlineRules), bulkPending = false, bulkLoaded = false, destroyed = false;
    const clear = document.createElement('button');
    clear.type = 'button'; clear.className = 'hs-clear'; clear.textContent = '×'; clear.setAttribute('aria-label', labels.clear); clear.hidden = !input.value;
    input.parentElement.append(clear);
    let focusTimer;
    let timer, controller, version = 0, opened = false, composing = false, suppressFocus = false;
    const cache = new Map();
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const home = document.createComment('Search location');
    form.before(home);
    const mobileDialog = document.createElement('dialog');
    mobileDialog.className = 'hs-mobile-search-dialog';
    mobileDialog.setAttribute('aria-label', panel.getAttribute('aria-label') || labels.suggestions);
    (document.getElementById('root') || document.body).append(mobileDialog);
    const back = document.createElement('button');
    back.type = 'button'; back.className = 'hs-mobile-back';
    back.setAttribute('aria-label', labels.close);
    const backIcon = document.createElement('i');
    backIcon.className = 'icon-arrow-left'; backIcon.setAttribute('aria-hidden', 'true');
    back.append(backIcon);
    input.parentElement.prepend(back);
    const leaveMobile = () => {
      if (!mobileDialog.open) return;
      suppressFocus = true;
      mobileDialog.close();
      home.after(form); form.classList.remove('hs-mobile-window');
      root.classList.remove('hs-mobile-search-open');
      suppressFocus = false;
    };
    const position = () => {
      if (!opened) return;
      const useMobile = document.documentElement.clientWidth <= 760 && typeof mobileDialog.showModal === 'function';
      if (useMobile) {
        if (!mobileDialog.open) {
          suppressFocus = true;
          form.classList.add('hs-mobile-window'); mobileDialog.append(form);
          root.classList.add('hs-mobile-search-open');
          mobileDialog.showModal(); input.focus({preventScroll:true});
          suppressFocus = false;
        }
        mobileDialog.style.setProperty('--hs-dialog-height', (viewport ? viewport.height : window.innerHeight) + 'px');
        mobileDialog.style.setProperty('--hs-dialog-top', (viewport ? viewport.offsetTop : 0) + 'px');
        return;
      }
      leaveMobile();
      const rect = input.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const mobile = viewportWidth <= 760;
      const width = mobile ? viewportWidth - 16 : Math.min(Number(form.dataset.panelWidth) || 1120, viewportWidth - 48);
      const left = mobile ? 8 : Math.max(24, Math.min(rect.left + rect.width / 2 - width / 2, viewportWidth - width - 24));
      // Anchor the panel directly below the input on every screen size.
      const top = rect.bottom + 8;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      panel.style.setProperty('--hs-left', left + 'px');
      panel.style.setProperty('--hs-top', top + 'px');
      panel.style.setProperty('--hs-width', width + 'px');
      panel.style.setProperty('--hs-height', Math.max(80, bottom - top - 12) + 'px');
    };
    const open = () => {
      if (!enabled) return;
      opened = true; panel.hidden = false; form.classList.add('hs-open');
      input.setAttribute('aria-expanded', 'true'); position();
    };
    const cancel = () => {
      clearTimeout(timer); version++;
      if (controller) controller.abort();
      panel.removeAttribute('aria-busy');
    };
    const close = (returnFocus = false) => {
      clearTimeout(focusTimer);
      cancel(); opened = false; leaveMobile(); panel.hidden = true; form.classList.remove('hs-open', 'processing');
      input.setAttribute('aria-expanded', 'false'); status.textContent = '';
      // Clear legacy overlay classes if another theme component set them.
      root.classList.remove('search-full', 'search-full-mode', 'search-focus');
      if (returnFocus) { suppressFocus = true; input.focus({preventScroll:true}); suppressFocus = false; }
    };
    back.addEventListener('click', () => close(true));
    mobileDialog.addEventListener('cancel', e => { e.preventDefault(); close(true); });
    const localizedDestination = raw => {
      const safe = safeDestination(raw, location.href);
      if (!safe) return null;
      const url = new URL(safe);
      const rootPath = (form.dataset.rootUrl || '/').replace(/\/$/, '');
      // Canonical handles stay unchanged; retain the selected language route.
      if (rootPath && /^\/(collections|products|pages|blogs)(\/|$)/.test(url.pathname)) {
        url.pathname = rootPath + url.pathname;
      }
      return url.href;
    };
    const allURL = term => {
      const url = new URL(form.action, location.href); url.searchParams.set('q', term); return url.href;
    };
    const node = (tag, className, text) => {
      const el = document.createElement(tag); if (className) el.className = className;
      if (text !== undefined) el.textContent = text; return el;
    };
    const decorate = (anchor, term) => {
      const text = anchor.textContent;
      anchor.replaceChildren();
      // Emphasize what the suggestion adds, not the repeated prefix.
      if (text.toLocaleLowerCase().startsWith(term.toLocaleLowerCase())) {
        anchor.append(document.createTextNode(text.slice(0, term.length)), node('strong', '', text.slice(term.length)));
      } else anchor.append(node('strong', '', text));
    };
    const addRules = (term) => {
      const group = panel.querySelector('[data-hs-queries]');
      if (!group) return;
      const list = group.querySelector('ul');
      const collectionIndex = new Map();
      const indexTemplate = panel.querySelector('[data-hs-collection-index]');
      for (const a of Array.from(indexTemplate?.content.querySelectorAll('a[href]') || [])) {
        const key = normalize(a.textContent);
        const destination = safeDestination(a.getAttribute('href'), location.href);
        if (!destination) continue;
        // Ambiguous identical titles should keep the search destination.
        collectionIndex.set(key, collectionIndex.has(key) ? null : destination);
      }
      const seen = new Set(); let count = 0;
      const frag = document.createDocumentFragment();
      list.querySelectorAll('[data-hs-custom]').forEach(a => a.parentElement.remove());
      for (const rule of findRules(ruleIndex, term)) {
        const destination = rule.destination ? localizedDestination(rule.destination) : allURL(rule.query);
        const key = normalize(rule.title);
        if (!destination || seen.has(key)) continue;
        seen.add(key); count++;
        const a = node('a', '', rule.title); a.href = destination; a.dataset.hsCustom = 'true'; decorate(a, term);
        const li = node('li'); li.append(a); frag.append(li);
      }
      for (const a of Array.from(list.querySelectorAll('a'))) {
        const key = normalize(a.textContent);
        if (seen.has(key) || count >= 5) { a.parentElement.remove(); continue; }
        const collectionURL = collectionIndex.get(key);
        if (collectionURL && a.hasAttribute('data-hs-auto')) {
          a.href = collectionURL; a.dataset.hsCollection = 'true';
        }
        seen.add(key); count++; decorate(a, term);
      }
      list.prepend(frag); group.hidden = count === 0;
    };
    const loadingPanel = (term, isError = false) => {
      const wrapper = node('div', 'hs-results');
      const head = node('div', 'hs-panel-heading'); head.append(node('p', '', term));
      const button = node('button', 'hs-close', '×'); button.type = 'button'; button.dataset.hsClose = ''; button.setAttribute('aria-label', labels.close); head.append(button);
      const group = node('div', 'hs-local'); group.dataset.hsQueries = '';
      group.append(node('p', 'hs-heading', labels.suggestions), node('ul', 'hs-links hs-queries'));
      wrapper.append(head, group, node('p', 'hs-message', isError ? labels.error : labels.loading));
      if (isError) {
        const all = node('a', 'hs-all', labels.all + ' →'); all.href = allURL(term);
        wrapper.append(all);
      }
      panel.replaceChildren(wrapper); addRules(term);
    };
    const render = (markup, term, requestVersion) => {
      if (requestVersion !== version || term !== input.value.trim() || !opened) return;
      panel.innerHTML = markup; addRules(term); panel.removeAttribute('aria-busy'); status.textContent = labels.ready; position();
    };
    const request = async (term, requestVersion) => {
      if (requestVersion !== version || !opened) return;
      if (cache.has(term)) { render(cache.get(term), term, requestVersion); return; }
      controller = new AbortController();
      const currentController = controller;
      const timeout = setTimeout(() => currentController.abort(), 8000);
      const url = new URL(form.dataset.endpoint, location.href);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[limit]', String(Math.min(10, Math.max(1, Number(form.dataset.productLimit) || 10))));
      url.searchParams.set('resources[limit_scope]', 'each');
      // Preserve this store's existing SKU / barcode / tag lookup behaviour.
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku,variants.barcode');
      url.searchParams.set('section_id', 'livesearch');
      try {
        const response = await fetch(url, {signal:currentController.signal, credentials:'same-origin'});
        if (!response.ok) throw new Error('Search unavailable');
        const text = await response.text();
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-livesearch');
        if (!section || !section.querySelector('.hs-results')) throw new Error('Missing section');
        if (requestVersion !== version) return;
        if (cache.size >= 20) cache.delete(cache.keys().next().value);
        cache.set(term, section.innerHTML); render(section.innerHTML, term, requestVersion);
      } catch {
        if (requestVersion === version && opened) {
          loadingPanel(term, true); panel.removeAttribute('aria-busy'); status.textContent = labels.error;
        }
      } finally { clearTimeout(timeout); }
    };
    const ensureBulkSuggestions = () => {
      if (bulkPending || bulkLoaded || !form.dataset.suggestionsUrl) return;
      bulkPending = true;
      loadSuggestionFile(form.dataset.suggestionsUrl, form.dataset.suggestionsLanguage).then(bulk => {
        bulkPending = false;
        const cached = suggestionFiles.get(form.dataset.suggestionsUrl + '|' + form.dataset.suggestionsLanguage);
        bulkLoaded = !cached?.failed;
        if (destroyed) return;
        ruleIndex = indexRules([...inlineRules, ...bulk]);
        // Never replace a link while someone is focusing/clicking it.
        if (opened && input.value.trim().length >= 2 && !panel.contains(document.activeElement)) {
          addRules(input.value.trim()); position();
        }
      });
    };
    const update = (delay = 250) => {
      clear.hidden = !input.value; cancel();
      if (!enabled || composing) return;
      ensureBulkSuggestions();
      const term = input.value.trim(); open();
      if (term.length < 2) { panel.innerHTML = emptyHTML; status.textContent = ''; position(); return; }
      const requestVersion = version;
      if (cache.has(term)) { render(cache.get(term), term, requestVersion); return; }
      loadingPanel(term); panel.setAttribute('aria-busy', 'true'); status.textContent = labels.loading;
      timer = setTimeout(() => request(term, requestVersion), delay);
    };
    input.addEventListener('input', () => update());
    input.addEventListener('search', () => update());
    input.addEventListener('compositionstart', () => { composing = true; cancel(); });
    input.addEventListener('compositionend', () => { composing = false; update(); });
    input.addEventListener('focus', () => { if (!suppressFocus) update(0); });
    input.addEventListener('click', () => { if (!opened) update(0); });
    clear.addEventListener('click', () => { input.value = ''; suppressFocus = true; input.focus(); suppressFocus = false; update(0); });
    form.addEventListener('click', e => { if (e.target.closest('[data-hs-close]')) close(true); });
    form.addEventListener('submit', () => { close(); });
    form.addEventListener('keydown', e => {
      if (e.isComposing) return;
      if (e.key === 'Escape' && opened) { e.preventDefault(); e.stopPropagation(); close(true); return; }
      if (!['ArrowDown','ArrowUp'].includes(e.key)) return;
      if (!opened) { update(0); e.preventDefault(); return; }
      const links = Array.from(panel.querySelectorAll('a[href]')).filter(a => a.getClientRects().length && !a.closest('[hidden]'));
      if (!links.length) return;
      const index = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? index + 1 : (index === -1 ? links.length - 1 : index - 1);
      e.preventDefault();
      if (next < 0 || next >= links.length) { suppressFocus = true; input.focus(); suppressFocus = false; }
      else links[next].focus();
    });
    const outside = e => { if (opened && !form.contains(e.target)) close(); };
    // During blur, activeElement may briefly be body. A microtask can run before
    // the browser focuses the clicked link and hide it before its click fires.
    const focusout = e => {
      clearTimeout(focusTimer);
      if (!e.relatedTarget || form.contains(e.relatedTarget)) return;
      focusTimer = setTimeout(() => { if (opened && !form.contains(document.activeElement)) close(); }, 0);
    };
    const pagehide = () => close();
    window.addEventListener('pagehide', pagehide);
    document.addEventListener('pointerdown', outside);
    form.addEventListener('focusout', focusout);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, {passive:true});
    viewport?.addEventListener('resize', position);
    viewport?.addEventListener('scroll', position);
    const sectionUnload = e => { if (e.target.contains(home)) form.hsSearchDestroy?.(); };
    document.addEventListener('shopify:section:unload', sectionUnload);
    form.hsSearchDestroy = () => {
      destroyed = true; close(); document.removeEventListener('shopify:section:unload', sectionUnload); mobileDialog.remove(); home.remove(); document.removeEventListener('pointerdown', outside);
      window.removeEventListener('pagehide', pagehide);
      window.removeEventListener('resize', position); window.removeEventListener('scroll', position);
      viewport?.removeEventListener('resize', position); viewport?.removeEventListener('scroll', position);
    };
  }
  const init = () => document.querySelectorAll('[data-hs-search]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', e => e.target.querySelector('[data-hs-search]')?.hsSearchDestroy?.());
})();
